import { Helmet } from 'react-helmet-async';
import Calculator from '../components/Calculator';
import RelatedCalculators from '../components/RelatedCalculators';
import { detectLocaleSync } from '../locales/config';

const COLORS = { bg: "#0a0a0f", text: "#e8e8f0", muted: "#9999b5", accent: "#00e5a0", border: "#1e1e2e" };

const faqItems = [
  {
    q: 'How does a car loan payoff calculator work?',
    a: "Enter your remaining loan balance, interest rate (APR), and current monthly payment. The calculator shows exactly when you'll pay off the loan and how much total interest you'll pay — plus how extra payments change both numbers.",
  },
  {
    q: 'Is it worth paying off a car loan early?',
    a: "It depends on your interest rate. If your car loan is above 5–6%, early payoff saves meaningful money and improves your debt-to-income ratio. If it's very low (2–3%), the saved interest may be small — but freeing up the monthly payment can still be worthwhile.",
  },
  {
    q: 'Will paying off my car loan early hurt my credit?',
    a: 'Paying off any installment loan closes the account, which can slightly reduce your credit mix and average account age. For most people this effect is minor and temporary — the freed-up cash flow typically outweighs any short-term score dip.',
  },
  {
    q: 'How much interest will I save by making extra payments?',
    a: 'Use the Extra Payment Scenarios section in the calculator for exact figures. On a $20,000 car loan at 7% APR with 5 years remaining, adding $100/month extra saves roughly $1,200 in interest and cuts about 11 months off your payoff.',
  },
  {
    q: 'Can I add other debts alongside my car loan?',
    a: 'Yes — click "+ Add debt" to include credit cards, personal loans, or any other debts. The calculator builds a combined payoff plan so you can see the full picture.',
  },
];

export default function CarLoanCalculator() {
  const locale = detectLocaleSync();
  return (
    <div style={{ background: COLORS.bg, minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", color: COLORS.text }}>
      <Helmet>
        <title>Free Car Loan Payoff Calculator — ClearMyDebt</title>
        <meta name="description" content="Calculate exactly when you'll pay off your car loan. Enter your balance, APR, and monthly payment — see your debt-free date and how much interest extra payments save." />
        <link rel="canonical" href="https://clearmydebt.app/car-loan-payoff-calculator/" />
        <meta property="og:title" content="Free Car Loan Payoff Calculator — ClearMyDebt" />
        <meta property="og:description" content="See your car loan payoff date and how much interest extra payments save. Free, instant, no signup." />
        <meta property="og:url" content="https://clearmydebt.app/car-loan-payoff-calculator/" />
        <meta property="og:image" content="https://clearmydebt.app/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://clearmydebt.app/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqItems.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        })}</script>
      </Helmet>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '48px 24px 0' }}>
        <div style={{ fontSize: '11px', letterSpacing: '3px', color: COLORS.accent, fontWeight: 700, marginBottom: '12px' }}>
          <a href="/" style={{ color: COLORS.accent, textDecoration: 'none' }}>ClearMyDebt</a>
        </div>
        <h1 style={{ fontSize: 'clamp(24px,5vw,36px)', fontWeight: 900, letterSpacing: '-1px', margin: '0 0 12px', background: `linear-gradient(135deg,#fff,${COLORS.accent})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Free Car Loan Payoff Calculator
        </h1>
        <p style={{ color: COLORS.muted, fontSize: '1.05rem', marginBottom: 0, lineHeight: 1.6 }}>
          Enter your car loan balance, interest rate, and monthly payment to see exactly
          when you'll be debt-free — and how much interest you'll save by paying a little extra each month.
        </p>
      </div>

      <Calculator
        defaultDebtType="Car Loan"
        lockedDebtType={false}
        locale={locale}
        showHelmet={false}
        showHeader={false}
      />

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 24px 64px' }}>
        <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 12px' }}>
          How to use this calculator
        </h2>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 28 }}>
          Enter your remaining loan balance (not the original amount), your interest rate (APR),
          and your current monthly payment. Then explore the "Extra Payment" scenarios to see
          how much faster you can pay off the loan — and exactly how many dollars in interest
          you'll save.
        </p>

        <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 12px' }}>
          How car loan interest works
        </h2>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 28 }}>
          Car loans use simple interest: each month's interest is calculated on your remaining
          principal balance. The average new-car loan APR in the US is around 7–9% (2025), while
          used cars typically run 10–14%. On a $25,000 loan at 8% over 6 years, total interest
          paid is about $6,500 — extra payments reduce that directly, since they lower the balance
          on which future interest is charged.
        </p>

        <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 20px' }}>
          Frequently Asked Questions
        </h2>
        {faqItems.map(({ q, a }) => (
          <div key={q} style={{ marginBottom: 20, padding: '16px', background: '#13131a', borderRadius: 10, border: `1px solid ${COLORS.border}` }}>
            <h3 style={{ color: COLORS.text, fontSize: '1rem', fontWeight: 600, margin: '0 0 8px' }}>{q}</h3>
            <p style={{ color: COLORS.muted, lineHeight: 1.6, margin: 0 }}>{a}</p>
          </div>
        ))}

      </div>

      <RelatedCalculators locale={locale} exclude="/car-loan-payoff-calculator/" />
    </div>
  );
}
